"use client";

import { LeadThrough, TestDriveRequestStatus } from "@alba-cars/common-modules";
import parsePhoneNumberFromString from "libphonenumber-js";
import { z } from "zod";

import { marketingDataSchema } from "./marketingDataSchema";

const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

// Type for validation message translator (e.g. from useTranslations("CarDetails.testDriveModal"))
export type TestDriveValidationTranslator = (key: string) => string;

const DEFAULT_MESSAGES = {
  nameRequired: "Name is required",
  nameMinLength: "Name must be at least 2 characters",
  nameMaxLength: "Name must be less than 255 characters",
  mobileRequired: "Mobile number is required",
  mobileInvalid: "Please enter a valid mobile number",
  emailInvalid: "Invalid email format",
  vehicleIdRequired: "Vehicle ID is required",
  dateInvalid: "Please select a valid date within the next 30 days",
  timeInvalid: "Please enter a valid time in the format HH:MM am/pm",
};

const getMessages = (t?: TestDriveValidationTranslator): typeof DEFAULT_MESSAGES =>
  t
    ? (Object.fromEntries(
        (Object.keys(DEFAULT_MESSAGES) as (keyof typeof DEFAULT_MESSAGES)[]).map((k) => [k, t(k)]),
      ) as typeof DEFAULT_MESSAGES)
    : DEFAULT_MESSAGES;

/** Creates testDriveSchema with optional translated validation messages */
export const createTestDriveSchema = (t?: TestDriveValidationTranslator) => {
  const m = getMessages(t);
  return z.object({
    testerName: z
      .string({ required_error: m.nameRequired })
      .min(2, m.nameMinLength)
      .max(255, m.nameMaxLength)
      .transform((str) => str.trim())
      .refine((val) => val.length > 0, m.nameRequired),

    testerMobile: z
      .string({ required_error: m.mobileRequired })
      .min(1, m.mobileRequired)
      .refine((value) => {
        try {
          const phoneNumber = parsePhoneNumberFromString(value);
          if (!phoneNumber) return false;
          return phoneNumber.isValid() && phoneNumber.isPossible();
        } catch {
          return false;
        }
      }, m.mobileInvalid),

    testerEmail: z.string().email(m.emailInvalid).regex(emailRegex, m.emailInvalid).optional().or(z.literal("")),

    vehicle: z.object({
      existingId: z.string().min(1, m.vehicleIdRequired),
    }),

    requestedDate: z.string({ required_error: m.dateInvalid }).refine(
      (date) => {
        const selectedDate = new Date(date);
        const today = new Date();
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(today.getDate() + 30);
        return selectedDate >= today && selectedDate <= thirtyDaysFromNow && !isNaN(selectedDate.getTime());
      },
      { message: m.dateInvalid },
    ),

    selectedTime: z
      .string({ required_error: m.timeInvalid })
      .refine((time) => /^(0?[1-9]|1[0-2]):[0-5][0-9] (am|pm)$/i.test(time), { message: m.timeInvalid }),

    status: z.nativeEnum(TestDriveRequestStatus),
    leadThrough: z.nativeEnum(LeadThrough),

    marketing: marketingDataSchema,
  });
};

/** Default schema (English) for backward compatibility */
export const testDriveSchema = createTestDriveSchema();

export type TestDriveFormData = z.infer<ReturnType<typeof createTestDriveSchema>>;
